'use client';

/**
 * ToolTimeoutSubmitted — terminal "we got it" card of the tool-timeout
 * recovery flow (lib/tool-timeout-view.ts's SUBMITTED_MESSAGE /
 * SUBMITTED_CALLBACK). Mirrors handoff-timeout-submitted.tsx's COPY-record
 * shape, but interpolates the actual email/phone the user just gave (the
 * design's exact copy: "...reply to {email}..." / "...ring {phone}
 * anytime..."). Reuses SupportRequestType/CallbackWindow from the shared
 * data layer (lib/api/support-requests.ts) rather than inventing parallel
 * enums — MESSAGE/CALLBACK and ANYTIME/MORNING/AFTERNOON/EVENING are exactly
 * those concepts already.
 */
import { Check } from 'lucide-react';
import { STANDARD_CARD_TONE, StandardCard } from './primitives/standard-card';
import { CallbackWindow, SupportRequestType } from '@/lib/api/support-requests';

interface ToolTimeoutSubmittedProps {
  type: SupportRequestType;
  /** SUBMITTED_MESSAGE only. */
  email?: string;
  /** SUBMITTED_CALLBACK only. */
  phone?: string;
  /** SUBMITTED_CALLBACK only — which window the user picked (or, on the
   *  "details already on file" one-tap confirm, the implicit ANYTIME that
   *  path always submits — see tool-timeout-callback.tsx). Defaults to
   *  ANYTIME so a call site that hasn't threaded this through still reads
   *  naturally, matching the copy this component shipped with originally. */
  callbackWindow?: CallbackWindow;
  /** Forwarded straight to the underlying StandardCard — see
   *  tool-timeout-choose.tsx's identical prop for the full "why". Omitted
   *  by text chat. */
  className?: string;
}

/** "...will ring {phone} {phrase}." — the ONLY thing that varies by window;
 *  the phone number and the trailing "No need to wait here." stay put for
 *  all four so the sentence keeps reading naturally regardless of which was
 *  picked. */
const CALLBACK_WINDOW_PHRASE: Record<CallbackWindow, string> = {
  [CallbackWindow.ANYTIME]: 'anytime',
  [CallbackWindow.MORNING]: 'in the morning',
  [CallbackWindow.AFTERNOON]: 'in the afternoon',
  [CallbackWindow.EVENING]: 'in the evening',
};

export function ToolTimeoutSubmitted({
  type,
  email,
  phone,
  callbackWindow = CallbackWindow.ANYTIME,
  className,
}: ToolTimeoutSubmittedProps) {
  const isMessage = type === SupportRequestType.MESSAGE;

  return (
    <StandardCard
      tone={STANDARD_CARD_TONE.SUCCESS}
      icon={
        <Check aria-hidden="true" className="size-[15px]" strokeWidth={2.5} />
      }
      eyebrow={isMessage ? 'MESSAGE SENT' : 'CALL-BACK REQUESTED'}
      title={isMessage ? "Your note's on its way" : "We'll call you back"}
      className={className}
    >
      {isMessage
        ? `A live agent will reply to ${email ?? 'the email you shared'}, usually within a few hours.`
        : `A live agent will ring ${phone ?? 'the number you shared'} ${CALLBACK_WINDOW_PHRASE[callbackWindow]}. No need to wait here.`}
    </StandardCard>
  );
}
